import React, { useCallback, useEffect, useState } from 'react';
import { useRecordContext, useTranslate } from 'react-admin';
import { Box, Button, Card, CardContent, Chip, Typography } from '@mui/material';
import { Print } from '@mui/icons-material';
import { apiClient, type ApiError } from '../../utils/apiClient';
import { API_CONFIG } from '../../config/api';
import { printerDetailLabel } from './printerDetail';
import type { PrintJob } from './types';

const PAGE_SIZE = 25;

const OUTCOME_COLOR: Record<PrintJob['outcome'], 'success' | 'warning' | 'error'> = {
    printed: 'success',
    partly_printed: 'warning',
    not_printed: 'error',
};

interface PrintJobPage {
    data: PrintJob[];
    total: number;
    limit: number;
    offset: number;
}

const COLUMNS = ['time', 'slip', 'result', 'reason', 'receipt'] as const;

export const KioskPrintLogPanel = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const [rows, setRows] = useState<PrintJob[]>([]);
    const [total, setTotal] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const kioskId = record ? String(record.id) : null;

    const loadPage = useCallback(async (offset: number) => {
        if (kioskId === null) return;
        setLoading(true);
        try {
            const page = await apiClient.get<PrintJobPage>(`${API_CONFIG.endpoints.backend.kioskDevices}/${kioskId}/print-jobs?limit=${PAGE_SIZE}&offset=${offset}`);
            setRows((current) => (offset === 0 ? page.data : [...current, ...page.data]));
            setTotal(page.total);
            setError(null);
            setLoaded(true);
        } catch (failure) {
            setError((failure as ApiError).message);
        } finally {
            setLoading(false);
        }
    }, [kioskId]);

    useEffect(() => {
        loadPage(0);
    }, [loadPage]);

    if (kioskId === null) return null;

    const text = (key: string) => translate(`resources.kiosk-devices.printLog.${key}`);
    const cell = (column: typeof COLUMNS[number], content: React.ReactNode) => (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>{text(`columns.${column}`)}</Typography>
            {content}
        </Box>
    );

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Print sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>{text('title')}</Typography>
                </Box>
                {error !== null && (
                    <Typography variant="body2" color="error" data-testid="print-log-error" sx={{ mb: 1 }}>
                        {translate('resources.kiosk-devices.printLog.loadFailed', { error })}
                    </Typography>
                )}
                {loaded && rows.length === 0 && <Typography variant="body2" color="text.secondary">{text('empty')}</Typography>}
                {rows.length > 0 && (
                    <Box>
                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 2, pb: 1 }}>
                            {COLUMNS.map((column) => (
                                <Typography key={column} variant="caption" color="text.secondary" sx={{ flex: 1 }}>{text(`columns.${column}`)}</Typography>
                            ))}
                        </Box>
                        {rows.map((row) => (
                            <Box
                                key={row.id}
                                data-testid={`print-job-${row.id}`}
                                sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.5, sm: 2 }, py: 1, borderTop: '1px solid', borderColor: 'divider' }}
                            >
                                {cell('time', <Typography variant="body2">{new Date(row.kioskAt).toLocaleString()}</Typography>)}
                                {cell('slip', (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Typography variant="body2">{text(`kind.${row.kind}`)}</Typography>
                                        {row.reprint && <Chip size="small" variant="outlined" label={text('copy')} />}
                                    </Box>
                                ))}
                                {cell('result', <Chip size="small" color={OUTCOME_COLOR[row.outcome]} label={text(`outcome.${row.outcome}`)} />)}
                                {cell('reason', <Typography variant="body2">{row.failureReason === null ? '-' : printerDetailLabel(translate, row.failureReason)}</Typography>)}
                                {cell('receipt', <Typography variant="body2">{row.receiptReference === null ? '-' : row.receiptReference}</Typography>)}
                            </Box>
                        ))}
                    </Box>
                )}
                {rows.length < total && (
                    <Button sx={{ mt: 1 }} variant="outlined" size="small" disabled={loading} onClick={() => loadPage(rows.length)}>
                        {text('loadMore')}
                    </Button>
                )}
            </CardContent>
        </Card>
    );
};
